import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Pause } from 'lucide-react'
import { apiGet } from '@/api/axios-client'
import { qk } from '@/api/query-keys'
import { ToneBadge } from '@/components/composites/status-badge'
import type { Client, Member } from '@/types/entities'
import { cn } from '@/lib/utils'
import { deadlineTitle, formatDuration, lateness, MILESTONE_PENDING, SEVERITY_SHORT, severityTone, type MilestoneView, type Severity, type SupportCase } from './api'

/** Re-renders every 30 seconds so countdowns move without refetching. */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}

export function useMembersById() {
  const query = useQuery({ queryKey: qk.members(), queryFn: () => apiGet<{ items: Member[] }>('members'), staleTime: 5 * 60_000 })
  return useMemo(() => {
    const map = new Map<string, Member>()
    for (const m of query.data?.items ?? []) {
      map.set(m.id, m)
      map.set(m.userId, m)
    }
    return { map, list: (query.data?.items ?? []).filter((m) => !m.disabled) }
  }, [query.data])
}

export function useClients() {
  const query = useQuery({ queryKey: qk.clients(), queryFn: () => apiGet<{ items: Client[] }>('clients'), staleTime: 5 * 60_000 })
  return query.data?.items ?? []
}

export function SeverityBadge({ severity, size = 'sm', className }: { severity: Severity | null; size?: 'xs' | 'sm' | 'md'; className?: string }) {
  return (
    <ToneBadge tone={severityTone(severity)} emphasis={severity === 'P1' ? 'solid' : 'soft'} size={size} className={className}>
      {severity ? SEVERITY_SHORT[severity] : 'Untriaged'}
    </ToneBadge>
  )
}

/** Time left (or over) on a milestone, ticking. Wall-clock between refetches; the server is exact. */
export function remainingNow(m: MilestoneView, now: number): number | null {
  if (!m.deadlineAt) return null
  return Date.parse(m.deadlineAt) - now
}

export function Countdown({ c, now, compact = false }: { c: SupportCase; now: number; compact?: boolean }) {
  const next = c.clock.next
  if (c.state === 'closed' || c.state === 'dismissed') return <span className="text-tiny text-text-muted">{c.state === 'closed' ? 'Closed' : 'Dismissed'}</span>
  if (!next) return <span className="text-tiny text-text-muted">All milestones met</span>
  if (next.status === 'paused' || c.waiting) {
    return (
      <span className="inline-flex items-center gap-1 text-tiny font-medium text-text-secondary">
        <Pause className="size-3" /> Waiting on customer
      </span>
    )
  }
  const left = remainingNow(next, now) ?? 0
  const over = left < 0
  const risk = !over && left <= Math.min(3_600_000, next.target / 2)
  return (
    <span className={cn('inline-flex flex-col leading-tight', compact ? 'items-end text-right' : '')} title={deadlineTitle(next.milestone, next.deadlineAt, now)}>
      <span className={cn('num text-sm font-semibold', over ? 'text-danger' : risk ? 'text-warning' : 'text-foreground')}>{lateness(left)}</span>
      <span className="text-tiny text-text-muted">{MILESTONE_PENDING[next.milestone]}</span>
    </span>
  )
}

export function ageText(ms: number): string {
  return formatDuration(ms)
}
